// Berita utilities for generating news options and content
import linkupService from '../../services/linkupService'
import { MODEL_MODES } from '../../constants/agentConfig'

// Generate news options based on query/topic using LLM and news sources
export const generateBeritaOptions = async (topic, llmService, language = 'id') => {
  let newsContext = ''
  let newsSources = []
  
  try {
    // Search for recent news about the topic
    console.log(`🔍 Mencari berita terbaru untuk topik: ${topic}`);
    
    // Determine search query based on language and topic
    let searchQuery = topic
    if (language === 'id') {
      // For Indonesian, prioritize Indonesian sources
      searchQuery = `${topic} berita terbaru Indonesia site:detik.com OR site:kompas.com OR site:tempo.co OR site:liputan6.com OR site:cnnindonesia.com OR site:republika.co.id OR site:antaranews.com OR site:okezone.com OR site:tribunnews.com OR site:suara.com`
    } else {
      // For English, use international sources
      searchQuery = `${topic} latest news site:bbc.com OR site:cnn.com OR site:reuters.com OR site:ap.org OR site:bloomberg.com OR site:theguardian.com OR site:nytimes.com OR site:washingtonpost.com OR site:wsj.com OR site:npr.org`
    }
    
    const searchResults = await linkupService.getScientificInfo(searchQuery);
    
    if (searchResults && searchResults.sources && searchResults.sources.length > 0) {
      newsSources = searchResults.sources.slice(0, 20); // Keep 20 sources for news generation
      const topSources = newsSources.slice(0, 8);
      newsContext = `\n\nKonteks Berita Terbaru:\n${topSources.map((source, index) => 
        `${index + 1}. ${source.title}\n   ${source.snippet}\n   URL: ${source.url}`
      ).join('\n\n')}`;;
      console.log(`✅ Ditemukan ${newsSources.length} sumber berita untuk opsi berita`);
    }
  } catch (error) {
    console.warn('❌ Error saat mencari berita:', error.message);
  }
  
  const languagePrompt = language === 'id' ? 
    'dalam bahasa Indonesia' : 'in English language'
  
  const prompt = `Berdasarkan topik "${topic}" dan konteks berita terbaru yang tersedia, buatkan 4-5 opsi berita yang berbeda ${languagePrompt} dalam format JSON dengan struktur berikut:
  {
    "options": [
      {
        "id": "option_1",
        "title": "Judul berita yang menarik dan informatif",
        "angle": "Sudut pandang atau fokus berita",
        "description": "Deskripsi singkat isi berita yang akan dibuat",
        "category": "Kategori berita (Politik, Ekonomi, Teknologi, Olahraga, dll)",
        "urgency": "high/medium/low",
        "estimatedLength": "800-1200 kata"
      }
    ]
  }
  
  PENTING: Ini adalah OPSI BERITA yang akan dipilih user, BUKAN konten berita lengkap!
  
  Pedoman:
  - Setiap opsi harus memiliki sudut pandang yang berbeda dari topik yang sama
  - Fokus pada berita yang aktual, terbaru, dan relevan
  - Judul harus menarik dan sesuai kaidah jurnalistik
  - Angle harus spesifik dan unik untuk setiap opsi
  - Category harus sesuai dengan jenis berita
  - Urgency berdasarkan tingkat kepentingan berita
  - Estimasi panjang artikel berita yang umum (800-1200 kata)
  - Pastikan setiap opsi dapat dikembangkan menjadi artikel berita lengkap
  - Gunakan informasi dari konteks berita terbaru jika tersedia${newsContext}
  
  Berikan HANYA JSON tanpa penjelasan tambahan:`
  
  try {
    // Use GENIUS mode for comprehensive news analysis
    const response = await llmService.sendMessage(
      [{ role: 'user', content: prompt }],
      { 
        mode: MODEL_MODES.GENIUS,
        searchType: 'web',
        stream: false,
        consumeCredits: false
      }
    )
    const cleanedResponse = response.replace(/```json|```/g, '').trim()
    const result = JSON.parse(cleanedResponse)
    
    if (result && result.options && Array.isArray(result.options)) {
      // Process and validate options
      const processedOptions = result.options.map((option, index) => ({
        id: option.id || `option_${index + 1}`,
        title: option.title,
        angle: option.angle || '',
        description: option.description || '',
        category: option.category || 'Umum',
        urgency: option.urgency || 'medium',
        estimatedLength: option.estimatedLength || '800-1200 kata',
        topic: topic,
        language: language
      }))
      
      return {
        options: processedOptions,
        sources: newsSources
      }
    } else {
      throw new Error('Format opsi berita tidak valid')
    }
  } catch (error) {
    console.warn('❌ Error generating news options with LLM, using fallback:', error.message);
    // Fallback to default options if LLM fails
    return {
      options: getDefaultNewsOptions(topic, language),
      sources: newsSources
    }
  }
}

// Fallback news options function
const getDefaultNewsOptions = (topic, language) => {
  const isIndonesian = language === 'id'
  
  if (isIndonesian) {
    return [
      {
        id: 'option_1',
        title: `Perkembangan Terbaru ${topic}: Analisis Mendalam`,
        angle: 'Analisis komprehensif perkembangan terkini',
        description: `Berita mendalam tentang perkembangan terbaru terkait ${topic} dengan analisis dari berbagai sudut pandang`,
        category: 'Analisis',
        urgency: 'high',
        estimatedLength: '1000-1200 kata',
        topic: topic,
        language: language
      },
      {
        id: 'option_2',
        title: `Dampak ${topic} Terhadap Masyarakat Indonesia`,
        angle: 'Fokus pada dampak sosial dan ekonomi',
        description: `Laporan tentang bagaimana ${topic} mempengaruhi kehidupan masyarakat Indonesia`,
        category: 'Sosial',
        urgency: 'medium',
        estimatedLength: '800-1000 kata',
        topic: topic,
        language: language
      },
      {
        id: 'option_3',
        title: `Respons Pemerintah Terhadap ${topic}`,
        angle: 'Kebijakan dan langkah pemerintah',
        description: `Berita tentang respons dan kebijakan pemerintah terkait ${topic}`,
        category: 'Politik',
        urgency: 'high',
        estimatedLength: '900-1100 kata',
        topic: topic,
        language: language
      },
      {
        id: 'option_4',
        title: `Perspektif Ahli: ${topic} dalam Konteks Global`,
        angle: 'Pandangan ahli dan perbandingan internasional',
        description: `Analisis ahli tentang ${topic} dengan perbandingan situasi di negara lain`,
        category: 'Internasional',
        urgency: 'medium',
        estimatedLength: '1000-1200 kata',
        topic: topic,
        language: language
      }
    ]
  } else {
    return [
      {
        id: 'option_1',
        title: `Latest Developments in ${topic}: In-Depth Analysis`,
        angle: 'Comprehensive analysis of recent developments',
        description: `Breaking news coverage of the latest developments regarding ${topic} with multi-perspective analysis`,
        category: 'Analysis',
        urgency: 'high',
        estimatedLength: '1000-1200 words',
        topic: topic,
        language: language
      },
      {
        id: 'option_2',
        title: `Global Impact of ${topic}: What You Need to Know`,
        angle: 'Focus on international implications',
        description: `Report on how ${topic} is affecting global markets, politics, and society`,
        category: 'International',
        urgency: 'medium',
        estimatedLength: '800-1000 words',
        topic: topic,
        language: language
      },
      {
        id: 'option_3',
        title: `Expert Opinions on ${topic}: Diverse Perspectives`,
        angle: 'Expert analysis and commentary',
        description: `Compilation of expert opinions and analysis on ${topic} from various fields`,
        category: 'Opinion',
        urgency: 'medium',
        estimatedLength: '900-1100 words',
        topic: topic,
        language: language
      },
      {
        id: 'option_4',
        title: `${topic}: Future Implications and Predictions`,
        angle: 'Forward-looking analysis',
        description: `Analysis of potential future implications and expert predictions regarding ${topic}`,
        category: 'Analysis',
        urgency: 'medium',
        estimatedLength: '1000-1200 words',
        topic: topic,
        language: language
      }
    ]
  }
}

// Generate complete news article content
export const generateBeritaContent = async (option, originalTopic, llmService, newsSources = [], language = 'id') => {
  const isIndonesian = language === 'id'
  
  // Prepare sources context
  let sourcesText = ''
  if (newsSources && newsSources.length > 0) {
    // Use relevant sources for the news article
    const relevantSources = newsSources.filter(source => {
      const optionKeywords = `${option.title} ${option.angle} ${option.description}`.toLowerCase().split(' ')
      const sourceText = `${source.title} ${source.snippet}`.toLowerCase()
      return optionKeywords.some(keyword => 
        keyword.length > 3 && sourceText.includes(keyword)
      )
    })
    
    // Use up to 15 sources for comprehensive coverage
    const maxSources = Math.min(15, newsSources.length)
    const sourcesToUse = relevantSources.length > 0 ? relevantSources.slice(0, maxSources) : newsSources.slice(0, maxSources)
    
    if (sourcesToUse.length > 0) {
      sourcesText = sourcesToUse.map((source, index) => 
        `[${index + 1}] ${source.title}\n${source.snippet}\nURL: ${source.url}\n`
      ).join('\n')
      console.log(`✅ Menggunakan ${sourcesToUse.length} sumber untuk berita: ${option.title}`)
    }
  }
  
  const languageInstruction = isIndonesian ? 
    'dalam bahasa Indonesia yang baik dan benar sesuai kaidah jurnalistik Indonesia' :
    'in proper English following international journalism standards'
  
  const structureGuide = isIndonesian ? `
STRUKTUR BERITA WAJIB:
1. **Judul Utama** - Menarik dan informatif
2. **Lead/Teras Berita** - Paragraf pembuka yang menjawab 5W+1H (What, Who, When, Where, Why, How)
3. **Body Berita** - Pengembangan informasi dengan:
   - Kutipan dari sumber terpercaya
   - Data dan fakta pendukung
   - Latar belakang konteks
   - Analisis situasi
4. **Penutup** - Kesimpulan atau proyeksi ke depan

KAIDAH PENULISAN BERITA:
- Gunakan piramida terbalik (informasi penting di awal)
- Kalimat pendek dan jelas
- Bahasa objektif dan netral
- Hindari opini pribadi
- Sertakan kutipan langsung dari narasumber
- Gunakan data dan statistik yang akurat` :
    `
NEWS STRUCTURE REQUIREMENTS:
1. **Headline** - Compelling and informative
2. **Lead** - Opening paragraph answering 5W+1H (What, Who, When, Where, Why, How)
3. **Body** - Information development with:
   - Quotes from credible sources
   - Supporting data and facts
   - Background context
   - Situation analysis
4. **Conclusion** - Summary or future projections

JOURNALISM STANDARDS:
- Use inverted pyramid structure (important info first)
- Short and clear sentences
- Objective and neutral language
- Avoid personal opinions
- Include direct quotes from sources
- Use accurate data and statistics`
  
  const basePrompt = isIndonesian ?
    `Tulis ARTIKEL BERITA LENGKAP ${languageInstruction} dengan judul "${option.title}" dan fokus pada sudut pandang: "${option.angle}".` :
    `Write a COMPLETE NEWS ARTICLE ${languageInstruction} with the title "${option.title}" focusing on the angle: "${option.angle}".`
  
  const guidelines = `
${structureGuide}

Pedoman WAJIB:
- LANGSUNG tulis artikel berita lengkap tanpa kalimat pembuka atau basa-basi
- TARGET PANJANG: ${option.estimatedLength}
- Kategori: ${option.category}
- Tingkat Urgensi: ${option.urgency}
- Topik Utama: "${originalTopic}"
- Deskripsi: ${option.description}
- Gunakan format yang mudah dibaca dengan paragraf yang terstruktur
- WAJIB: Sertakan kutipan dari narasumber (bisa berdasarkan sumber yang tersedia)
- WAJIB: Berikan konteks dan latar belakang yang memadai
- WAJIB: Gunakan data dan fakta untuk mendukung berita
- WAJIB: Akhiri dengan kesimpulan atau proyeksi yang relevan
- Pastikan berita bersifat aktual dan terkini
- Hindari sensasionalisme, tetap objektif dan berimbang
- Gunakan bahasa yang sesuai untuk pembaca umum`
  
  let prompt
  if (sourcesText) {
    prompt = `${basePrompt}

SUMBER BERITA YANG HARUS DIGUNAKAN:
${sourcesText}

PASTIKAN untuk:
- Mengintegrasikan informasi dari sumber-sumber di atas ke dalam berita
- Memberikan sitasi dalam format [1], [2], dst. sesuai urutan sumber
- Menggunakan kutipan langsung dari sumber jika memungkinkan
- Memverifikasi dan cross-check informasi dari berbagai sumber
- Menyajikan informasi secara berimbang dan objektif

${guidelines}

Tulis artikel berita sekarang:`
  } else {
    prompt = `${basePrompt}

Karena tidak ada sumber khusus yang tersedia, gunakan pengetahuan umum yang akurat dan terpercaya untuk menulis berita. Pastikan informasi yang disajikan faktual dan dapat dipertanggungjawabkan.

${guidelines}

Tulis artikel berita sekarang:`
  }
  
  try {
    // Use GENIUS mode for high-quality news content generation
    const response = await llmService.sendMessage(
      [{ role: 'user', content: prompt }],
      { 
        mode: MODEL_MODES.GENIUS,
        maxTokens: 4000,
        searchType: 'web',
        stream: false,
        consumeCredits: false
      }
    )
    
    if (!response || response.trim().length === 0) {
      throw new Error(`Konten kosong untuk berita: ${option.title}`)
    }
    
    console.log(`📝 Berita "${option.title}" selesai dibuat`)
    
    return response
  } catch (error) {
    console.error(`❌ Error generating news content for "${option.title}":`, error)
    throw new Error(`Gagal membuat konten berita: ${option.title}. ${error.message}`)
  }
}

// Count words in text
export const countWords = (text) => {
  if (!text || typeof text !== 'string') return 0
  return text.trim().split(/\s+/).filter(word => word.length > 0).length
}

// Validate news options
export const validateNewsOptions = (options) => {
  if (!Array.isArray(options) || options.length === 0) {
    return { isValid: false, message: 'Tidak ada opsi berita yang tersedia' }
  }
  
  for (const option of options) {
    if (!option.title || !option.angle || !option.description) {
      return { isValid: false, message: 'Opsi berita tidak lengkap' }
    }
  }
  
  return { isValid: true, message: 'Opsi berita valid' }
}

// Get urgency color for styling
export const getUrgencyColor = (urgency) => {
  const colors = {
    'high': 'text-red-500 bg-red-500/10',
    'medium': 'text-yellow-500 bg-yellow-500/10',
    'low': 'text-green-500 bg-green-500/10'
  }
  
  return colors[urgency] || 'text-gray-500 bg-gray-500/10'
}

// Get category color for styling
export const getCategoryColor = (category) => {
  const colors = {
    'Politik': 'text-blue-500 bg-blue-500/10',
    'Ekonomi': 'text-green-500 bg-green-500/10',
    'Teknologi': 'text-purple-500 bg-purple-500/10',
    'Olahraga': 'text-orange-500 bg-orange-500/10',
    'Sosial': 'text-pink-500 bg-pink-500/10',
    'Internasional': 'text-indigo-500 bg-indigo-500/10',
    'Analisis': 'text-teal-500 bg-teal-500/10',
    'Opinion': 'text-amber-500 bg-amber-500/10',
    'Analysis': 'text-teal-500 bg-teal-500/10',
    'International': 'text-indigo-500 bg-indigo-500/10'
  }
  
  return colors[category] || 'text-gray-500 bg-gray-500/10'
}

// Parse markdown content and extract citations
export const parseMarkdownContent = (content) => {
  if (!content) return { parsedContent: '', citations: [] }
  
  // Extract citations like [1], [2], etc.
  const citationPattern = /\[(\d+)\]/g
  const citations = []
  let match
  
  while ((match = citationPattern.exec(content)) !== null) {
    const citationNumber = parseInt(match[1])
    if (!citations.includes(citationNumber)) {
      citations.push(citationNumber)
    }
  }
  
  // Return the content as-is for markdown parsing and extracted citations
  return {
    parsedContent: content,
    citations: citations.sort((a, b) => a - b)
  }
}

// Format source to APA style for news references
export const formatSourceToAPA = (source, index) => {
  if (!source) return ''
  
  const currentYear = new Date().getFullYear()
  let domain = 'Unknown'
  
  try {
    if (source.url) {
      domain = new URL(source.url).hostname.replace('www.', '')
    }
  } catch (error) {
    domain = 'Unknown'
  }
  
  // Extract author/publication from domain or title
  let publication = domain
  
  // Map common Indonesian news domains to proper publication names
  const publicationMap = {
    'detik.com': 'Detik.com',
    'kompas.com': 'Kompas.com',
    'tempo.co': 'Tempo.co',
    'liputan6.com': 'Liputan6.com',
    'cnnindonesia.com': 'CNN Indonesia',
    'republika.co.id': 'Republika Online',
    'antaranews.com': 'Antara News',
    'okezone.com': 'Okezone.com',
    'tribunnews.com': 'Tribunnews.com',
    'suara.com': 'Suara.com',
    'bbc.com': 'BBC News',
    'cnn.com': 'CNN',
    'reuters.com': 'Reuters',
    'ap.org': 'Associated Press',
    'bloomberg.com': 'Bloomberg',
    'theguardian.com': 'The Guardian',
    'nytimes.com': 'The New York Times',
    'washingtonpost.com': 'The Washington Post',
    'wsj.com': 'The Wall Street Journal',
    'npr.org': 'NPR'
  }
  
  publication = publicationMap[domain] || domain.charAt(0).toUpperCase() + domain.slice(1)
  
  // Format: Publication. (Year, Month Day). Title. URL
  const title = source.title || 'Untitled'
  const url = source.url || `https://${domain}`
  
  // Get current date for news articles (assuming recent news)
  const currentDate = new Date()
  const months = [
    'Januari', 'Februari', 'Maret', 'April', 'Mei', 'Juni',
    'Juli', 'Agustus', 'September', 'Oktober', 'November', 'Desember'
  ]
  const formattedDate = `${currentDate.getDate()} ${months[currentDate.getMonth()]} ${currentYear}`
  
  return `${publication}. (${formattedDate}). ${title}. <em>Diakses dari</em> <a href="${url}" target="_blank" rel="noopener noreferrer">${url}</a>`
}